import React from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { toast } from "react-toastify";
import { useNavigate, Link as RouterLink } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";
import { extractFirebaseErrorCode, firebaseErrorMessages } from "@/utils/firebaseErrorMessages";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { Loader2, Mail, Lock, LogIn } from "lucide-react";

import logo from '@/assets/icons/kurut-logo.png'

interface IFormInputs {
    email: string;
    password: string;
}

const SignIn: React.FC = () => {
    const navigate = useNavigate();
    const { login, loginWithGoogle, googleLoading, loginLoading } = useAuth();

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<IFormInputs>({
        mode: "onSubmit",
    });

    const onSubmit: SubmitHandler<IFormInputs> = async ({ email, password }) => {
        try {
            await login(email, password);
            toast.success("Вы успешно вошли!");
            navigate("/people");
        } catch (error: any) {
            const errorCode = extractFirebaseErrorCode(error.message);
            toast.error(
                firebaseErrorMessages[errorCode || ""] || "Ошибка при входе. Попробуйте снова."
            );
        }
    };

    const handleGoogleLogin = async () => {
        try {
            await loginWithGoogle();
            toast.success("Вы успешно вошли через Google!");
            navigate("/people");
        } catch (error: any) {
            const errorCode = extractFirebaseErrorCode(error.message);
            toast.error(
                firebaseErrorMessages[errorCode || ""] ||
                "Ошибка при входе через Google. Попробуйте снова."
            );
        }
    };

    return (
        <div className="mx-auto w-full max-w-md px-4 py-10 animate-fade-in">
            {/* Brand */}
            <div className="mb-6 flex flex-col items-center gap-3">
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl border bg-muted/20 shadow-sm">
                    <img
                        src={logo}
                        alt="KurutPhone"
                        className="h-10 w-10 object-contain"
                        draggable={false}
                    />
                </div>
                <div className="text-center">
                    <div className="text-2xl font-semibold tracking-tight">KurutPhone</div>
                    <div className="text-sm text-muted-foreground">Войдите в систему</div>
                </div>
            </div>

            <Card className="overflow-hidden">
                <CardHeader className="space-y-1 bg-muted/20">
                    <CardTitle className="flex items-center gap-2 text-base">
                        <LogIn className="h-4 w-4 text-muted-foreground" />
                        Вход
                    </CardTitle>
                </CardHeader>

                <CardContent className="p-5 space-y-5">
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        {/* Email */}
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Email</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    className={[
                                        "pl-9",
                                        errors.email ? "border-destructive focus-visible:ring-destructive" : "",
                                    ].join(" ")}
                                    placeholder="example@mail.com"
                                    inputMode="email"
                                    autoComplete="email"
                                    {...register("email", {
                                        required: "Введите адрес электронной почты",
                                        pattern: {
                                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                            message: "Некорректный формат email",
                                        },
                                    })}
                                />
                            </div>
                            {errors.email?.message ? (
                                <p className="text-xs text-destructive">{errors.email.message}</p>
                            ) : null}
                        </div>

                        {/* Password */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label className="text-xs text-muted-foreground">Пароль</Label>

                                <Button asChild variant="link" className="h-auto p-0 text-xs">
                                    <RouterLink to="/forgot-password">Забыли пароль?</RouterLink>
                                </Button>
                            </div>

                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    type="password"
                                    className={[
                                        "pl-9",
                                        errors.password ? "border-destructive focus-visible:ring-destructive" : "",
                                    ].join(" ")}
                                    placeholder="Введите пароль"
                                    autoComplete="current-password"
                                    {...register("password", {
                                        required: "Введите пароль",
                                        minLength: {
                                            value: 6,
                                            message: "Пароль должен быть не короче 6 символов",
                                        },
                                    })}
                                />
                            </div>

                            {errors.password?.message ? (
                                <p className="text-xs text-destructive">{errors.password.message}</p>
                            ) : null}
                        </div>

                        {/* Submit */}
                        <Button type="submit" className="w-full" disabled={loginLoading}>
                            {loginLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                            Войти
                        </Button>
                    </form>

                    <div className="space-y-3">
                        <div className="relative">
                            <Separator />
                            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-2 text-xs text-muted-foreground">
                или
              </span>
                        </div>

                        <Button
                            type="button"
                            variant="outline"
                            className="w-full"
                            onClick={handleGoogleLogin}
                            disabled={googleLoading}
                        >
                            {googleLoading ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <svg
                                    className="h-4 w-4 mr-2"
                                    viewBox="0 0 48 48"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M44.5 20H24v8.5h11.7C34.1 33.6 29.6 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.5 0 6.3 1.2 8.6 3.2l6-6C34.9 4.7 29.8 2.5 24 2.5 12.1 2.5 2.5 12.1 2.5 24S12.1 45.5 24 45.5 44.5 36.7 44.5 24c0-1.3-.1-2.7-.3-4z"
                                        fill="currentColor"
                                    />
                                </svg>
                            )}
                            Войти через Google
                        </Button>

                        <div className="text-center text-sm text-muted-foreground">
                            Нет аккаунта?{" "}
                            <Button asChild variant="link" className="h-auto p-0 text-sm font-medium">
                                <RouterLink to="/sign-up">Зарегистрироваться</RouterLink>
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default SignIn;
